import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Plus, Search, Shield, ShieldOff, Trash2, Edit2, 
  Eye, EyeOff, Key, Calendar, Mail, Phone, X, Check,
  QrCode, ChevronDown, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import ProducerLayout from '../../components/producer/ProducerLayout';
import NotificationBell from '../../components/NotificationBell';

const ProducerValidatorsPage = () => {
  const [validators, setValidators] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingValidator, setEditingValidator] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [tempPassword, setTempPassword] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    assigned_events: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchValidators();
    fetchEvents();
  }, []);

  const fetchValidators = async () => {
    try {
      const response = await api.get('/producers/me/validators');
      setValidators(response.data);
    } catch (error) {
      console.error('Error fetching validators:', error);
      toast.error('Error al cargar validadores');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/producers/me/events');
      setEvents(response.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'El nombre es requerido';
    if (!formData.email.trim()) errors.email = 'El email es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Email inválido';
    if (!editingValidator && !formData.password) errors.password = 'La contraseña es requerida';
    else if (!editingValidator && formData.password.length < 6) errors.password = 'Mínimo 6 caracteres';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (editingValidator) {
        // Update validator
        const updateData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          assigned_events: formData.assigned_events
        };
        await api.put(`/producers/me/validators/${editingValidator.id}`, updateData);
        toast.success('Validador actualizado');
      } else {
        // Create new validator
        await api.post('/producers/me/validators', formData);
        toast.success('Validador creado exitosamente');
      }
      
      fetchValidators();
      closeModal();
    } catch (error) {
      console.error('Error saving validator:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar validador');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (validator) => {
    try {
      await api.post(`/producers/me/validators/${validator.id}/toggle-status`);
      toast.success(validator.is_active ? 'Validador desactivado' : 'Validador activado');
      fetchValidators();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (validator) => {
    if (!window.confirm(`¿Eliminar a ${validator.name}? Esta acción no se puede deshacer.`)) return;
    
    try {
      await api.delete(`/producers/me/validators/${validator.id}`);
      toast.success('Validador eliminado');
      fetchValidators();
    } catch (error) {
      toast.error('Error al eliminar validador');
    }
  };

  const handleResetPassword = async (validator) => {
    try {
      const response = await api.post(`/producers/me/validators/${validator.id}/reset-password`);
      setTempPassword(response.data.temporary_password);
      setShowPasswordModal(validator);
    } catch (error) {
      toast.error('Error al restablecer contraseña');
    }
  };

  const openCreateModal = () => {
    setFormData({ name: '', email: '', password: '', phone: '', assigned_events: [] });
    setFormErrors({});
    setEditingValidator(null);
    setShowCreateModal(true);
  };

  const openEditModal = (validator) => {
    setFormData({
      name: validator.name,
      email: validator.email,
      password: '',
      phone: validator.phone || '',
      assigned_events: validator.assigned_events || []
    });
    setFormErrors({});
    setEditingValidator(validator);
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingValidator(null);
    setFormData({ name: '', email: '', password: '', phone: '', assigned_events: [] });
    setFormErrors({});
  };

  const toggleEventAssignment = (eventId) => {
    setFormData(prev => ({
      ...prev,
      assigned_events: prev.assigned_events.includes(eventId)
        ? prev.assigned_events.filter(id => id !== eventId)
        : [...prev.assigned_events, eventId]
    }));
  };

  const filteredValidators = validators.filter(v =>
    v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <ProducerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout>
      <div className="space-y-6 p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="text-violet-500" />
            Mis Validadores
          </h1>
          <p className="text-zinc-400 mt-1">
            Crea cuentas para que tu equipo valide entradas en tus eventos
          </p>
        </div>
        <div className="flex items-center gap-3">
          <NotificationBell 
            userType="producer" 
            apiEndpoint="/producer/notifications" 
            title="Notificaciones" 
          />
          <Button 
            onClick={openCreateModal}
            className="bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:from-violet-500 hover:to-pink-500"
            data-testid="create-validator-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Validador
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por nombre o email..."
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="validator-search"
        />
      </div>

      {/* Validators List */}
      {filteredValidators.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
          <Users size={48} className="mx-auto mb-4 text-zinc-600" />
          <h3 className="text-lg font-medium text-white mb-2">
            {validators.length === 0 ? 'No tenés validadores' : 'Sin resultados'}
          </h3>
          <p className="text-zinc-500 mb-6">
            {validators.length === 0 
              ? 'Crea validadores para que tu equipo pueda escanear entradas en tus eventos'
              : 'No se encontraron validadores con ese criterio'
            }
          </p>
          {validators.length === 0 && (
            <Button onClick={openCreateModal} className="bg-violet-500 text-black hover:bg-[#C4A030]">
              <Plus size={18} className="mr-2" />
              Crear primer validador
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredValidators.map((validator) => (
            <motion.div
              key={validator.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-zinc-900/50 border rounded-xl p-4 ${
                validator.is_active ? 'border-zinc-800' : 'border-red-900/50 bg-red-950/10'
              }`}
              data-testid={`validator-card-${validator.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Info */}
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    validator.is_active ? 'bg-violet-500/20' : 'bg-zinc-800'
                  }`}>
                    <QrCode size={24} className={validator.is_active ? 'text-violet-500' : 'text-zinc-500'} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white">{validator.name}</h3>
                      {!validator.is_active && (
                        <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full border border-red-500/30">
                          Desactivado
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Mail size={14} />
                        {validator.email}
                      </span>
                      {validator.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={14} />
                          {validator.phone}
                        </span>
                      )}
                    </div>
                    {/* Assigned Events */}
                    <div className="mt-2">
                      {validator.assigned_events?.includes('ALL_EVENTS') ? (
                        <span className="px-2 py-0.5 text-xs bg-violet-500/20 text-violet-500 rounded-full border border-violet-500/30">
                          ✨ Todos los eventos
                        </span>
                      ) : validator.assigned_events_names?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {validator.assigned_events_names.map(event => (
                            <span 
                              key={event.id}
                              className="px-2 py-0.5 text-xs bg-zinc-800 text-zinc-300 rounded-full"
                            >
                              {event.title}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-500 flex items-center gap-1">
                          <AlertCircle size={12} />
                          Sin eventos asignados
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(validator)}
                    className="text-zinc-400 hover:text-white"
                    data-testid={`edit-validator-${validator.id}`}
                  >
                    <Edit2 size={16} className="mr-1" />
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResetPassword(validator)}
                    className="text-zinc-400 hover:text-violet-500"
                    data-testid={`reset-password-${validator.id}`}
                  >
                    <Key size={16} className="mr-1" />
                    Contraseña
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleStatus(validator)}
                    className={validator.is_active ? 'text-zinc-400 hover:text-red-400' : 'text-zinc-400 hover:text-green-400'}
                    data-testid={`toggle-status-${validator.id}`}
                  >
                    {validator.is_active ? (
                      <>
                        <ShieldOff size={16} className="mr-1" />
                        Desactivar
                      </>
                    ) : (
                      <>
                        <Shield size={16} className="mr-1" />
                        Activar
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(validator)}
                    className="text-zinc-400 hover:text-red-400"
                    data-testid={`delete-validator-${validator.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">
                    {editingValidator ? 'Editar Validador' : 'Nuevo Validador'}
                  </h2>
                  <button onClick={closeModal} className="text-zinc-500 hover:text-white">
                    <X size={24} />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1">
                    Nombre *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Juan Pérez"
                    className={`bg-zinc-800 border-zinc-700 ${formErrors.name ? 'border-red-500' : ''}`}
                    data-testid="validator-name-input"
                  />
                  {formErrors.name && <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="juan@ejemplo.com"
                    className={`bg-zinc-800 border-zinc-700 ${formErrors.email ? 'border-red-500' : ''}`}
                    data-testid="validator-email-input"
                  />
                  {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
                </div>

                {/* Password (only for new validators) */}
                {!editingValidator && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1">
                      Contraseña *
                    </label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Mínimo 6 caracteres"
                      className={`bg-zinc-800 border-zinc-700 ${formErrors.password ? 'border-red-500' : ''}`}
                      data-testid="validator-password-input"
                    />
                    {formErrors.password && <p className="text-red-400 text-xs mt-1">{formErrors.password}</p>}
                  </div>
                )}

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1">
                    Teléfono (opcional)
                  </label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+54 11 1234-5678"
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="validator-phone-input"
                  />
                </div>

                {/* Assigned Events */}
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Eventos asignados
                  </label>
                  <p className="text-xs text-zinc-500 mb-3">
                    El validador solo podrá escanear entradas de los eventos seleccionados
                  </p>
                  
                  {/* Option for all events */}
                  <label
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors mb-3 ${
                      formData.assigned_events.includes('ALL_EVENTS')
                        ? 'bg-violet-500/20 border border-violet-500/50'
                        : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.assigned_events.includes('ALL_EVENTS')}
                      onChange={() => {
                        if (formData.assigned_events.includes('ALL_EVENTS')) {
                          setFormData(prev => ({ ...prev, assigned_events: [] }));
                        } else {
                          setFormData(prev => ({ ...prev, assigned_events: ['ALL_EVENTS'] }));
                        }
                      }}
                      className="sr-only"
                    />
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${
                      formData.assigned_events.includes('ALL_EVENTS')
                        ? 'bg-violet-500 text-black'
                        : 'bg-zinc-700'
                    }`}>
                      {formData.assigned_events.includes('ALL_EVENTS') && <Check size={14} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-violet-500">✨ Todos los eventos</p>
                      <p className="text-xs text-zinc-500">
                        Acceso a validar entradas de cualquier evento actual y futuro
                      </p>
                    </div>
                  </label>
                  
                  {/* Individual events (disabled if ALL_EVENTS is selected) */}
                  <div className={`space-y-2 max-h-48 overflow-y-auto pr-2 ${
                    formData.assigned_events.includes('ALL_EVENTS') ? 'opacity-50 pointer-events-none' : ''
                  }`}>
                    {events.length === 0 ? (
                      <p className="text-zinc-500 text-sm">No hay eventos disponibles</p>
                    ) : (
                      events.map(event => (
                        <label
                          key={event.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            formData.assigned_events.includes(event.id)
                              ? 'bg-violet-500/20 border border-violet-500/50'
                              : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.assigned_events.includes(event.id)}
                            onChange={() => toggleEventAssignment(event.id)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded flex items-center justify-center ${
                            formData.assigned_events.includes(event.id)
                              ? 'bg-violet-500 text-black'
                              : 'bg-zinc-700'
                          }`}>
                            {formData.assigned_events.includes(event.id) && <Check size={14} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{event.title}</p>
                            <p className="text-xs text-zinc-500">
                              {new Date(event.date).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                            </p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={closeModal}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-violet-500 text-black hover:bg-[#C4A030]"
                    data-testid="save-validator-btn"
                  >
                    {submitting ? 'Guardando...' : (editingValidator ? 'Guardar cambios' : 'Crear validador')}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Password Reset Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowPasswordModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Key size={32} className="text-violet-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Contraseña Restablecida</h3>
                <p className="text-zinc-400 mb-4">
                  La nueva contraseña temporal para <strong>{showPasswordModal.name}</strong> es:
                </p>
                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 mb-4">
                  <code className="text-2xl font-mono text-violet-500">{tempPassword}</code>
                </div>
                <p className="text-xs text-zinc-500 mb-6">
                  Compartí esta contraseña con el validador. Se recomienda que la cambie después de su primer inicio de sesión.
                </p>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(tempPassword);
                    toast.success('Contraseña copiada');
                  }}
                  variant="outline"
                  className="mr-2"
                >
                  Copiar
                </Button>
                <Button
                  onClick={() => setShowPasswordModal(null)}
                  className="bg-violet-500 text-black hover:bg-[#C4A030]"
                >
                  Cerrar
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </ProducerLayout>
  );
};

export default ProducerValidatorsPage;
